import { NextIntlClientProvider } from 'next-intl';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, normaliseLocale, SupportedLocale } from './config';
import { readLocaleCookie, writeLocaleCookie } from './cookies';
import { getMessages } from './load-messages';

interface AppLocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

const AppLocaleContext = createContext<AppLocaleContextValue | null>(null);

export const useAppLocale = (): AppLocaleContextValue => {
  const ctx = useContext(AppLocaleContext);
  if (!ctx) {
    throw new Error('useAppLocale must be used within LocaleProvider');
  }
  return ctx;
};

interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: SupportedLocale;
}

const resolveInitialLocale = (initialLocale?: SupportedLocale): SupportedLocale => {
  if (initialLocale) {
    return normaliseLocale(initialLocale);
  }
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  return readLocaleCookie() ?? DEFAULT_LOCALE;
};

export const LocaleProvider = ({ children, initialLocale }: LocaleProviderProps) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    resolveInitialLocale(initialLocale),
  );

  const setLocale = useCallback((next: SupportedLocale) => {
    const normalised = normaliseLocale(next);
    setLocaleState((current) => {
      if (normalised === current) {
        return current;
      }
      writeLocaleCookie(normalised);
      return normalised;
    });
  }, []);

  const ctxValue = useMemo<AppLocaleContextValue>(
    () => ({ locale, setLocale }),
    [locale, setLocale],
  );
  const messages = useMemo(() => getMessages(locale), [locale]);

  return (
    <AppLocaleContext.Provider value={ctxValue}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone="UTC">
        {children}
      </NextIntlClientProvider>
    </AppLocaleContext.Provider>
  );
};
